/**
 * The small ring that says how an upload is going — the same `ProgressRing`
 * as the printers' «Producción de hoy», at file-row size.
 *
 *  - uploading → the brand ring fills with the bytes sent;
 *  - retrying  → an amber arc turns (the connection dropped; it starts again
 *                by itself, `lib/upload/retry.ts`);
 *  - failed    → a red error mark (the row offers «Reintentar»).
 *
 * Shown at the RIGHT of the file, where it will stay (`FileAttachmentRow`) or
 * in the corner of its thumbnail (`PhotoThumb`); never in a separate panel.
 * `uploadCaption` is the text under the file name that goes with it.
 */
import React from 'react';
import ProgressRing from './ProgressRing';
import { Icon } from './Icon';
import { getUploadLabels } from '../lib/fileValidation';
import { formatBytes } from '../lib/bytes';
import type { UploadProgress } from '../lib/upload/retry';

export function uploadPercent(progress: UploadProgress): number | null {
    if (progress.kind !== 'uploading') return null;
    if (progress.total <= 0) return 0;
    return Math.max(0, Math.min(100, Math.floor((progress.loaded / progress.total) * 100)));
}

/** «45 % · 18 MB de 40 MB», «Sin conexión · se reintentará», «No se pudo subir». */
export function uploadCaption(progress: UploadProgress): string {
    const labels = getUploadLabels();
    if (progress.kind === 'uploading') {
        return labels.uploadProgress(uploadPercent(progress) ?? 0, formatBytes(progress.loaded), formatBytes(progress.total));
    }
    if (progress.kind === 'retrying') return progress.offline ? labels.uploadOffline : labels.uploadRetrying;
    // A refusal keeps its reason on the row: the toast goes, the row stays.
    if (progress.retryable === false && progress.message) return progress.message;
    return labels.uploadFailed;
}

export interface UploadIndicatorProps {
    progress: UploadProgress;
    /** `sm` 18 px (file rows), `md` 26 px (thumbnails). */
    size?: 'sm' | 'md';
    className?: string;
}

const UploadIndicator: React.FC<UploadIndicatorProps> = ({ progress, size = 'sm', className = '' }) => {
    const box = size === 'sm' ? 'h-[18px] w-[18px]' : 'h-[26px] w-[26px]';
    const caption = uploadCaption(progress);
    if (progress.kind === 'failed') {
        return (
            <span className={`inline-flex shrink-0 items-center justify-center ${box} ${className}`} title={progress.message || caption} aria-label={caption} role="img">
                <Icon name="error" className={`${size === 'sm' ? '!text-[18px]' : '!text-[24px]'} text-red-500`} />
            </span>
        );
    }
    const retrying = progress.kind === 'retrying';
    return (
        <span
            className={`inline-flex shrink-0 ${box} ${retrying ? 'motion-safe:animate-spin' : ''} ${className}`}
            role="progressbar"
            aria-label={caption}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={retrying ? undefined : uploadPercent(progress) ?? 0}
            title={caption}
        >
            <ProgressRing
                percent={retrying ? 25 : uploadPercent(progress)}
                stroke={14}
                className={retrying ? 'text-amber-500' : 'text-primary'}
            />
        </span>
    );
};

export default UploadIndicator;
